package dev.akshaychordiya.pages

import androidx.compose.runtime.Composable
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.data.add
import com.varabyte.kobweb.core.init.InitRoute
import com.varabyte.kobweb.core.init.InitRouteContext
import com.varabyte.kobweb.core.layout.Layout
import com.varabyte.kobweb.silk.components.navigation.Link
import com.varabyte.kobweb.silk.components.navigation.UncoloredLinkVariant
import com.varabyte.kobweb.silk.components.navigation.UndecoratedLinkVariant
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.style.toAttrs
import com.varabyte.kobweb.silk.style.CssStyle
import com.varabyte.kobweb.silk.style.base
import com.varabyte.kobweb.silk.style.breakpoint.Breakpoint
import com.varabyte.kobweb.silk.style.toModifier
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import dev.akshaychordiya.MetaTextStyle
import dev.akshaychordiya.components.layouts.PageLayoutData
import dev.akshaychordiya.components.widgets.CardStyle
import dev.akshaychordiya.components.widgets.PageHeader
import dev.akshaychordiya.components.widgets.Preview
import dev.akshaychordiya.components.widgets.PreviewShape
import dev.akshaychordiya.content.Book
import dev.akshaychordiya.content.NEXT_BOOK_TEASER
import dev.akshaychordiya.content.books
import dev.akshaychordiya.fadeIn
import dev.akshaychordiya.fadeUp
import dev.akshaychordiya.toSitePalette
import org.jetbrains.compose.web.css.DisplayStyle
import org.jetbrains.compose.web.css.FlexDirection
import org.jetbrains.compose.web.css.FlexWrap
import org.jetbrains.compose.web.css.cssRem
import org.jetbrains.compose.web.dom.Div

@InitRoute
fun initBooksPage(ctx: InitRouteContext) {
    ctx.data.add(PageLayoutData("Books"))
}

/** Cover above the text on a phone, beside it once there's room. */
val BookLayoutStyle = CssStyle {
    base {
        Modifier
            .fillMaxWidth()
            .display(DisplayStyle.Flex)
            .flexDirection(FlexDirection.Column)
            .gap(1.25.cssRem)
    }
    Breakpoint.SM { Modifier.flexDirection(FlexDirection.Row) }
}

@Composable
private fun BookCard(book: Book, index: Int) {
    val palette = ColorMode.current.toSitePalette()

    Div(CardStyle.toModifier().fadeUp(index).toAttrs()) {
        Div(BookLayoutStyle.toAttrs()) {
        book.coverUrl?.let { Preview(it, "${book.title} cover", PreviewShape.COVER) }
        Column(Modifier.fillMaxWidth().gap(0.5.cssRem)) {
            Row(Modifier.gap(0.6.cssRem).flexWrap(FlexWrap.Wrap)) {
                SpanText(
                    if (book.inProgress) book.year else "${book.year} · ${book.publisher}",
                    MetaTextStyle.toModifier(),
                )
                if (book.inProgress) {
                    SpanText(
                        "Work in progress",
                        MetaTextStyle.toModifier().color(palette.brandAccent),
                    )
                }
            }

            SpanText(book.title, Modifier.fontSize(1.4.cssRem).fontWeight(700).lineHeight(1.2))
            SpanText(book.subtitle, Modifier.fontSize(1.05.cssRem).color(palette.subdued))

            if (book.coAuthors.isNotEmpty()) {
                SpanText(
                    "With ${book.coAuthors.joinToString(" and ")}",
                    MetaTextStyle.toModifier().margin(top = 0.2.cssRem),
                )
            }

            SpanText(book.description, Modifier.margin(top = 0.5.cssRem).fontSize(1.cssRem))

            if (book.links.isNotEmpty()) {
                Row(Modifier.gap(1.25.cssRem).margin(top = 0.75.cssRem).flexWrap(FlexWrap.Wrap)) {
                    book.links.forEach { link ->
                        Link(
                            link.url,
                            link.label,
                            Modifier.fontSize(0.95.cssRem).fontWeight(500).color(palette.brandPrimary),
                            variant = UndecoratedLinkVariant.then(UncoloredLinkVariant),
                        )
                    }
                }
            }
            }
        }
    }
}

@Page
@Layout(".components.layouts.PageLayout")
@Composable
fun BooksPage() {
    PageHeader(
        eyebrow = "Books",
        title = "Books I've written",
        subtitle = "Long-form Kotlin, written properly.",
    )

    Column(Modifier.fillMaxWidth().gap(1.25.cssRem)) {
        books.forEachIndexed { index, book -> BookCard(book, index) }
    }

    if (NEXT_BOOK_TEASER.isNotEmpty()) {
        SpanText(
            NEXT_BOOK_TEASER,
            MetaTextStyle.toModifier().margin(top = 1.75.cssRem).fadeIn(index = books.size),
        )
    }
}
